import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { HERO_SLIDES, CONTACT } from "../data/content";

export const Hero = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Intervall hängt an `active`: ein manueller Wechsel startet den Timer neu,
  // Hover pausiert den automatischen Bildablauf.
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % HERO_SLIDES.length);
    }, 5500);
    return () => clearInterval(id);
  }, [paused, active]);

  return (
  <section
    data-testid="hero-section"
    className="relative min-h-[100svh] flex items-end lg:items-center overflow-hidden"
    onMouseEnter={() => setPaused(true)}
    onMouseLeave={() => setPaused(false)}
  >
    <div className="absolute inset-0" data-testid="hero-slider">
      {HERO_SLIDES.map((src, i) => (
        <img
          key={i}
          src={src}
          alt="Hochwertiger Holzboden im Wohnraum"
          className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out"
          style={{ opacity: active === i ? 1 : 0, transitionDuration: "1400ms" }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#2B2B30]/90 via-[#2B2B30]/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B30]/80 via-transparent to-transparent" />
    </div>

    <div className="container-blum relative z-10 pb-24 pt-36 lg:py-32 w-full">
      <div className="max-w-2xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="font-script text-3xl text-[#D7C4B1] mb-4"
        >
          sicher · boden · ständig
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight"
        >
          Böden, die bleiben.<br />
          <span className="text-[#D7C4B1]">Qualität, die man spürt.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
          className="text-lg text-white/85 mt-6 leading-relaxed max-w-xl"
        >
          Seit über 55 Jahren Ihr Meisterbetrieb für Parkett und Fußbodentechnik in Kalbach.
          Drei Generationen Erfahrung – und das Handwerk liegt uns noch immer am Herzen.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 mt-10"
        >
          <a href="#kontakt" data-testid="hero-cta-primary" className="btn-base btn-red">Termin anfragen</a>
          <a href={CONTACT.phoneHref} data-testid="hero-cta-phone" className="btn-base btn-ghost-light">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
        </motion.div>
      </div>
    </div>

    {/* Slider-Punkte */}
    <div data-testid="hero-dots" className="absolute bottom-8 right-5 lg:right-10 z-20 flex items-center gap-2.5">
      {HERO_SLIDES.map((_, i) => (
        <button
          key={i}
          onClick={() => setActive(i)}
          data-testid={`hero-dot-${i}`}
          aria-label={`Bild ${i + 1} anzeigen`}
          aria-current={active === i}
          className={`h-2.5 rounded-full backdrop-blur-sm border border-white/40 transition-all duration-300 ${
            active === i ? "w-7 bg-white/90" : "w-2.5 bg-white/25 hover:bg-white/50"
          }`}
        />
      ))}
    </div>

    <a href="#unternehmen" className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden lg:flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors">
      <span className="text-xs uppercase tracking-[0.2em] font-display">Entdecken</span>
      <ArrowDown className="w-4 h-4 animate-bounce" />
    </a>
  </section>
  );
};
